import React, { useState, useEffect } from "react"
import { ZoomIn, ChevronLeft, ChevronRight } from "react-feather"
import { Carousel } from "../Carousel"

export const BeforeAndAfters = ({ beforeAndAfters, treatmentName = "" }) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const [lightboxVisible, setLightboxVisible] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [previousSrc, setPreviousSrc] = useState<string | null>(null)
  const [previousVisible, setPreviousVisible] = useState(false)

  const total = beforeAndAfters.length
  const lightboxSrc = lightboxIndex >= 0 ? beforeAndAfters[lightboxIndex].image : ""
  const lightboxLabel = lightboxIndex % 2 === 0 ? "After" : "Before"

  const openLightbox = index => {
    setLightboxIndex(index)
    setLightboxOpen(true)
    setPreviousSrc(null)
    requestAnimationFrame(() => setLightboxVisible(true))
  }

  const closeLightbox = () => {
    setLightboxVisible(false)
    setTimeout(() => {
      setLightboxOpen(false)
      setLightboxIndex(-1)
      setPreviousSrc(null)
    }, 400)
  }

  const swapImage = delta => {
    setPreviousSrc(beforeAndAfters[lightboxIndex].image)
    setPreviousVisible(true)
    setLightboxIndex(i => (i + delta + total) % total)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setPreviousVisible(false))
    })
    setTimeout(() => setPreviousSrc(null), 320)
  }
  const goToPrev = () => swapImage(-1)
  const goToNext = () => swapImage(1)

  useEffect(() => {
    if (!lightboxOpen) {
      document.body.style.overflow = ""
      return
    }
    document.body.style.overflow = "hidden"
    const handleKey = e => {
      if (e.key === "Escape") closeLightbox()
      else if (e.key === "ArrowLeft") goToPrev()
      else if (e.key === "ArrowRight") goToNext()
    }
    window.addEventListener("keydown", handleKey)
    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handleKey)
    }
  }, [lightboxOpen, total])

  return (
    <div className="py-20">
      <div className="col-span-4 md:col-span-8 lg:col-span-8 lg:col-start-3">
        <p className="my-0 overline">Dr Hawkes&apos; Results</p>
        <h2 className="fluid-heading-05 lg:text-left">Before &amp; <i>Afters</i></h2>
      </div>
      <div className="col-span-12 lg:col-span-8 lg:col-start-3 baCarousel">
        <Carousel
          settingsOverrides={{
            slidesToShow: 3.5,
            responsive: [
              {
                breakpoint: 1024,
                settings: { slidesToShow: 3.5 },
              },
              {
                breakpoint: 768,
                settings: { slidesToShow: 1.5 },
              },
            ],
          }}
        >
          {beforeAndAfters.map(({ image, imageAlt }, index) => {
            const label = index % 2 === 0 ? "After" : "Before"
            return (
              <div
                key={index}
                className="flex flex-col h-full col-span-12 md:col-span-10 md:col-start-2 lg:col-start-4 lg:col-span-3"
              >
                <div
                  className="relative group cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  {/* Top bar */}
                  <div
                    className="absolute top-0 left-0 right-0 z-10"
                    style={{ height: "26%", backgroundColor: "#0a0a1e" }}
                  />
                  {/* Zoom icon */}
                  {/* Image */}
                  <img
                    className="w-full block"
                    style={{ aspectRatio: "1/1", objectFit: "cover" }}
                    src={image}
                    alt={
                      imageAlt ||
                      `${label} — patient result by Dr Elizabeth Hawkes`
                    }
                  />
                  {/* Bottom bar with label */}
                  <div
                    className="absolute bottom-0 left-0 right-0 z-10 flex items-center px-4"
                    style={{ height: "26%", backgroundColor: "#0a0a1e" }}
                  >
                    <p className="mb-0 text-white font-mono text-sm uppercase tracking-wider">
                      {label}
                    </p>
                    <span className="flex items-center gap-1 ml-auto px-2 py-1 text-white font-mono text-xs tracking-wider" style={{ border: '1px solid rgba(255,255,255,0.6)', opacity: 0.8 }}>
                      <ZoomIn size="12" />
                      Zoom
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </Carousel>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.95)",
            opacity: lightboxVisible ? 1 : 0,
            zIndex: 1000,
            transition: "opacity 400ms ease-out",
          }}
          onClick={closeLightbox}
        >
          {/* Prev arrow */}
          <button
            className="absolute text-white hover:opacity-100 transition-opacity p-2"
            style={{ top: "50%", left: "8px", transform: "translateY(-50%)", opacity: 0.6, zIndex: 10 }}
            onClick={e => { e.stopPropagation(); goToPrev() }}
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </button>
          {/* Next arrow */}
          <button
            className="absolute text-white hover:opacity-100 transition-opacity p-2"
            style={{ top: "50%", right: "8px", transform: "translateY(-50%)", opacity: 0.6, zIndex: 10 }}
            onClick={e => { e.stopPropagation(); goToNext() }}
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </button>
          {/* Content */}
          <div
            className="flex flex-col max-w-2xl w-full px-12"
            style={{
              transform: lightboxVisible ? "scale(1)" : "scale(0.96)",
              opacity: lightboxVisible ? 1 : 0,
              transition: "opacity 400ms ease-out, transform 400ms ease-out",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button above image, right-aligned */}
            <div className="flex justify-end mb-4">
              <button
                className="text-white hover:opacity-100 transition-opacity"
                onClick={closeLightbox}
                aria-label="Close"
                style={{ fontSize: "22px", lineHeight: 1, opacity: 0.6 }}
              >
                ✕
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <img
                className="w-full block"
                src={lightboxSrc}
                alt={
                  (lightboxIndex >= 0 &&
                    beforeAndAfters[lightboxIndex].imageAlt) ||
                  `${lightboxLabel} — patient result by Dr Elizabeth Hawkes`
                }
              />
              {previousSrc && (
                <img
                  className="w-full block"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: previousVisible ? 1 : 0,
                    transition: "opacity 280ms ease-out",
                    pointerEvents: "none",
                  }}
                  src={previousSrc}
                  alt=""
                />
              )}
            </div>
            {/* Caption below image */}
            <p className="mt-4 mb-0 text-white opacity-50 font-mono text-xs tracking-widest uppercase">
              Client {lightboxLabel.toLowerCase()} image
              {treatmentName ? ` — ${treatmentName}` : ""}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
